import type { ReactNode } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { RiArrowLeftLine, RiArrowRightSLine, RiPlugLine } from '@remixicon/react';
import type { AccessibleTenant, ConnectorDto, MeResponse } from '@atnt/shared';
import { ContentPanel, NoAccess, PageHeader, PageLayout } from '@/app/shell';
import { ApiError } from '@/shared/api/client';
import { Button, EmptyState, Skeleton, SubTabs, type SubTabItem } from '@/shared/ui';
import { LiveProvider } from '@/features/constructor/engine';
import { ErrorBanner } from '@/features/devices/components/parts';
import { useConnector, useConnectorsContext } from './api';
import { useRowActions } from './ConnectorsPage';
import { LogTab } from './components/LogTab';
import { OverviewTab } from './components/OverviewTab';
import { ConnectorStatusBadge, ScopeBadge, TypeChip } from './components/parts';
import { SensorsTab } from './components/SensorsTab';
import { SettingsTab } from './components/SettingsTab';
import { ChannelsCard } from './components/unified';
import { connectorRights } from './permissions';

export const CONNECTOR_TABS = ['overview', 'sensors', 'channels', 'settings', 'log'] as const;
export type ConnectorTab = (typeof CONNECTOR_TABS)[number];

/**
 * One connector (`/t/:slug/connectors/:id`): header (back to the registry, type, name, status, scope; Tekshirish ·
 * Yoqish/O'chirish · O'chirish), sub-tabs "Umumiy" · "Datchiklar" · "Kanallar" (ATNT unified server only — the real
 * `hydro:<KEY>` channel mapping) · "Sozlamalar" · "Jurnal".
 */
export function ConnectorDetailPage({
  tenant,
  me,
  connectorId,
  tab,
  onTab,
}: {
  tenant: AccessibleTenant;
  me: MeResponse;
  connectorId: string;
  tab: ConnectorTab;
  onTab: (tab: ConnectorTab) => void;
}) {
  const { t } = useTranslation('connectors');
  const navigate = useNavigate();
  const rights = connectorRights(tenant, me);
  const ctx = useConnectorsContext(tenant, me);
  const query = useConnector(ctx, connectorId);
  const back = () => void navigate({ to: '/t/$tenantSlug/connectors', params: { tenantSlug: tenant.slug } });
  const actions = useRowActions(ctx, rights, { onDeleted: back });

  if (!rights.read) return <NoAccess />;
  const c = query.data;

  if (!c) {
    const notFound = query.error instanceof ApiError && query.error.status === 404;
    return (
      <>
        <PageHeader title={<Crumbs onBack={back}>{query.isLoading ? <Skeleton className="h-16 w-200" /> : null}</Crumbs>} />
        <PageLayout>
          <ContentPanel className={notFound ? 'items-center justify-center' : undefined}>
            {notFound ? (
              <EmptyState icon={RiPlugLine} title={t('detail.notFound')} description={t('detail.notFoundHint')} action={{ label: t('detail.back'), icon: RiArrowLeftLine, onClick: back }} />
            ) : query.isError ? (
              <ErrorBanner onRetry={() => void query.refetch()} />
            ) : (
              <div className="grid grid-cols-3 gap-10">
                <Skeleton className="h-220 rounded-12" />
                <Skeleton className="h-220 rounded-12" />
                <Skeleton className="h-220 rounded-12" />
              </div>
            )}
          </ContentPanel>
        </PageLayout>
      </>
    );
  }

  const unified = c.type === 'atnt_unified';
  const active: ConnectorTab = tab === 'channels' && !unified ? 'overview' : tab;
  const items: SubTabItem[] = [
    { id: 'overview', label: t('detail.tabs.overview') },
    { id: 'sensors', label: t('detail.tabs.sensors'), count: c.sensorsTotal },
    ...(unified ? [{ id: 'channels', label: t('detail.tabs.channels') }] : []),
    { id: 'settings', label: t('detail.tabs.settings') },
    { id: 'log', label: t('detail.tabs.log') },
  ];
  const headerActions = actions(c).flatMap((a) => (a.type === 'separator' || a.type === 'label' ? [] : [a]));

  const page = (
    <>
      <PageHeader
        title={
          <Crumbs onBack={back}>
            <TypeChip type={c.type} size={24} />
            <h1 className="min-w-0 truncate t-page-title text-fg">{c.name}</h1>
            <ConnectorStatusBadge status={c.status} />
            <ScopeBadge scope={c.scope} />
          </Crumbs>
        }
        actions={
          headerActions.length ? (
            <>
              {headerActions.map((a) => (
                <Button key={a.key} size="sm" variant={a.danger ? 'destructive-soft' : 'secondary'} leftIcon={a.icon} disabled={a.disabled} onClick={() => a.onSelect?.()}>
                  {a.label}
                </Button>
              ))}
            </>
          ) : null
        }
      />
      <PageLayout subbar={<SubTabs aria-label={t('detail.tabsLabel')} items={items} activeId={active} onSelect={(id) => onTab(id as ConnectorTab)} />}>
        <ContentPanel className={active === 'overview' || active === 'settings' ? 'gap-10' : undefined}>
          <TabBody tab={active} c={c} tenant={tenant} ctx={ctx} rights={rights} onTab={onTab} />
        </ContentPanel>
      </PageLayout>
    </>
  );
  return unified ? <LiveProvider tenantId={tenant.id}>{page}</LiveProvider> : page;
}

function TabBody({
  tab,
  c,
  tenant,
  ctx,
  rights,
  onTab,
}: {
  tab: ConnectorTab;
  c: ConnectorDto;
  tenant: AccessibleTenant;
  ctx: ReturnType<typeof useConnectorsContext>;
  rights: ReturnType<typeof connectorRights>;
  onTab: (tab: ConnectorTab) => void;
}) {
  switch (tab) {
    case 'overview':
      return <OverviewTab c={c} ctx={ctx} rights={rights} onTab={onTab} />;
    case 'sensors':
      return <SensorsTab c={c} ctx={ctx} rights={rights} />;
    case 'channels':
      return <ChannelsCard tenant={tenant} editable={rights.operate} />;
    case 'settings':
      return <SettingsTab key={c.updatedAt} c={c} ctx={ctx} rights={rights} />;
    case 'log':
      return <LogTab c={c} ctx={ctx} />;
  }
}

/** "Konnektorlar ›" link + the connector identity in the page header. */
function Crumbs({ onBack, children }: { onBack: () => void; children?: ReactNode }) {
  const { t } = useTranslation('connectors');
  return (
    <div className="flex min-w-0 items-center gap-8">
      <button type="button" onClick={onBack} className="shrink-0 cursor-pointer rounded-4 t-page-title text-fg-secondary transition-colors hover:text-fg">
        {t('list.title')}
      </button>
      <RiArrowRightSLine aria-hidden className="size-16 shrink-0 text-icon" />
      {children}
    </div>
  );
}
